import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

import MainInput from '../inputs/MainInput';
import MainButtonInvert from '../buttons/MainButtonInvert';

// Keyframes for animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const scaleOut = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.9);
  }
`;

// Styled components with animations
const Background = styled.div`
  /* Existing styles */
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  z-index: 101;

  /* Animation */
  animation: ${(props) =>
    props.animationType === 'in' ? fadeIn : fadeOut}
    0.3s ease-in-out forwards;
`;

const Modal = styled.div`
  /* Existing styles */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  min-width: 400px;
  height: auto;
  background: white;
  border-radius: 15px;
  box-shadow: 0 0 10px var(--primary-color);
  border: 2px solid var(--primary-color);
  padding: 10px 10px 10px 15px;
  gap: 20px;

  /* Animation */
  animation: ${(props) =>
    props.animationType === 'in' ? scaleIn : scaleOut}
    0.3s ease-in-out forwards;
`;

const ModalHeader = styled.div`
  /* Existing styles */
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: auto;

  h3 {
    font-size: 30px;
    font-family: 'Modernist', sans-serif;
    color: black;
    margin: 0;
  }

  p {
    font-size: 20px;
    font-weight: 600;
    font-family: 'Modernist', sans-serif;
    color: black;
    margin: 0;
  }

  span {
    font-size: 20px;
    font-weight: 600;
    font-family: 'Modernist', sans-serif;
    color: var(--primary-color);
    cursor: pointer;
    transition: all 0.3s ease-in-out;
    text-decoration: underline;


    &:hover {
      color: var(--secondary-color);
    }
  }
`;

const CloseButton = styled.div`
  /* Existing styles */
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: black;
  cursor: pointer;
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: scale(1.1);
  }
`;

const ModalBody = styled.div`
  /* Existing styles */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: calc(100% - 10px);
  height: auto;
  gap: 20px;
  padding-right: 10px;
`;

const ModalEnd = styled.div`
  /* Existing styles */
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-end;
  width: calc(100% - 10px);
  height: auto;
  gap: 10px;
  padding-right: 10px;
`;


const InputModal = ({ header, subheader, inputLabel, input, setInput, onClose }) => {
  const [animationType, setAnimationType] = useState('in');

  const handleClose = () => {
    setAnimationType('out');
    setTimeout(() => {
      onClose();
    }, 300); // Match the animation duration
  };

  return (
    <Background animationType={animationType}>
      <Modal animationType={animationType}>
        <ModalHeader>
          <h3>{header}</h3>
          <CloseButton onClick={handleClose}>
            {/* SVG for close icon */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M18.3 5.71a1 1 0 0 0-1.42 0L12 10.59 7.12 5.71A1 1 0 0 0 5.7 7.12L10.59 12l-4.88 4.88a1 1 0 1 0 1.42 1.42L12 13.41l4.88 4.88a1 1 0 0 0 1.42-1.42L13.41 12l4.88-4.88a1 1 0 0 0 0-1.41z" />
            </svg>
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          <ModalHeader>
            <p>{subheader}</p>
          </ModalHeader>
          <MainInput
            label={inputLabel}
            value={input}
            onChange={(e) =>
              setInput(e.target.value)
            }
          />
        </ModalBody>
        <ModalEnd>
          <MainButtonInvert
            header="Enter"
            onClick={handleClose}
          />
        </ModalEnd>
      </Modal>
    </Background>
  );
};

export default InputModal;